// NPM Modules
import { writable } from 'svelte/store';
import collect from 'collect.js';
import dayjs from 'dayjs';
// Models
import Meeting from '@/Models/Meeting';
// Stores
import { mergeCollectionStore, mergeModelDataInCollectionStore } from '@/Stores/StoreHelpers.js';
import RaceStore from '@/Stores/Racing/RaceStore.js';
// Constants
import config from '@/config.js';

/**
 * Our Store can be used 2 ways in svelte components
 * 1) ($ prefix) $StoreName to reference writable store collection with all collect.js methods available
 * 2) (Direct) StoreName.method() to call a method on the store
 */
const init = () => {
	let _store;

	const { subscribe, set, update } = writable(collect());

	subscribe((current) => (_store = current));

	const methods = {
		/**
		 * ----------------- MODEL/API METHODS -----------------
		 */

		/**
		 * Fetch Meetings and optionally their Races, updating store collections
		 * @param {string} fromDate
		 * @param {string} toDate
		 * @param {boolean} reset
		 * @param {boolean} includeRaces - whether to include races and promotions
		 */
		getMeetings(
			fromDate = dayjs().format('YYYY-MM-DD'),
			toDate = dayjs().format('YYYY-MM-DD'),
			reset = false,
			includeRaces = false
		) {
			if (!config.RACING_ENABLED) return;

			const meeting = new Meeting();
			if (includeRaces) {
				meeting.with(['races', 'races.promotions']);
			}

			return meeting
				.where('from', fromDate, 'start_date')
				.where('to', toDate, 'start_date')
				.paginate(100, 1)
				.then((result) => {
					let allMeetings = collect();
					let allRaces = collect();

					result.data.map((meeting) => {
						if (includeRaces && meeting.races?.data) {
							meeting.races.data.map((race) => {
								race.meeting_id = meeting.id;
								delete race.meeting;
								allRaces.push(race);
							});
							delete meeting.races;
						}

						allMeetings.push(meeting);
					});

					if (reset) {
						set(allMeetings);
						if (includeRaces) RaceStore.set(allRaces);
					} else {
						mergeCollectionStore(this, allMeetings);
						if (includeRaces) mergeCollectionStore(RaceStore, allRaces);
					}
				})
				.catch((e) => {
					console.error(e);
				});
		},

		/**
		 * fetch single Meeting with Races and update our store collections
		 * @param {string} meetingId
		 */
		getMeetingWithRaces(meetingId) {
			if (!config.RACING_ENABLED) return;

			const meeting = new Meeting();
			return meeting
				.with(['races', 'races.promotions'])
				.find(meetingId)
				.then((meeting) => {
					// extract Race Models to their own collection, map to meeting_id
					let allRaces = collect();
					let allMeetings = collect();

					// extract races for this meeting
					meeting.races.data.map((race) => {
						race.meeting_id = meeting.id;
						delete race.meeting;

						allRaces.push(race);
					});
					delete meeting.races;

					allMeetings.push(meeting);

					mergeCollectionStore(this, allMeetings);
					mergeCollectionStore(RaceStore, allRaces);
					return meeting;
				})
				.catch((e) => {
					console.error(e);
				});
		},

		getSingleMeeting(meetingId) {
			if (!config.RACING_ENABLED) return;

			const meeting = new Meeting();
			return meeting.find(meetingId);
		},

		updateMeetingClassification(meetingId, classificationId, raceId = null) {
			if (!config.RACING_ENABLED) return;

			let meeting = _store.firstWhere('id', meetingId);
			return meeting
				.update({
					classification_id: classificationId
				})
				.then((results) => {
					if (raceId) RaceStore.getRaceWithBetTypes(raceId, meetingId);
					return results;
				});
		},

		updateMeetingVisibility(meetingId, data) {
			if (!config.RACING_ENABLED) return;

			return _store
				.firstWhere('id', meetingId)
				.update(data)
				.then((results) => {
					// keep our local collection in sync
					update((meetings) => {
						return meetings.each((meeting) => {
							if (meeting.id === meetingId) {
								meeting.visible = data.visible;
							}
							return meeting;
						});
					});
					return results;
				});
		},

		async updateMeetingBoostable(meetingId, boostable) {
			if (!config.RACING_ENABLED) return;

			return _store
				.firstWhere('id', meetingId)
				.update({ boostable })
				.then((results) => {
					// keep our local collection in sync
					update((meetings) => {
						return meetings.each((meeting) => {
							if (meeting.id === meetingId) {
								meeting.boostable = boostable;
							}
							return meeting;
						});
					});
					// we need to fetch all races again to get current boostable status
					this.getMeetingWithRaces(meetingId);
					return results;
				});
		},

		async updateMeetingBonusDisabled(meetingId, bonus_disabled) {
			if (!config.RACING_ENABLED) return;

			return _store
				.firstWhere('id', meetingId)
				.update({ bonus_disabled })
				.then((results) => {
					// keep our local collection in sync
					update((meetings) => {
						return meetings.each((meeting) => {
							if (meeting.id === meetingId) {
								meeting.bonus_disabled = bonus_disabled;
							}
							return meeting;
						});
					});
					// we need to fetch all races again to get current bonus disabled status
					this.getMeetingWithRaces(meetingId);
					return results;
				});
		},

		async updateMeetingCashoutEnabled(meetingId, cashout_enabled) {
			if (!config.RACING_ENABLED) return;

			return _store
				.firstWhere('id', meetingId)
				.update({ cashout_enabled: cashout_enabled })
				.then((results) => {
					// keep our local collection in sync
					update((meetings) => {
						return meetings.each((meeting) => {
							if (meeting.id === meetingId) {
								meeting.cashout_enabled = cashout_enabled;
							}
							return meeting;
						});
					});
					// we need to fetch all races again to get current bonus disabled status
					this.getMeetingWithRaces(meetingId);
					return results;
				});
		},

		applyMeetingUpdates(data) {
			if (_store.firstWhere('id', data.id.toString())) {
				mergeModelDataInCollectionStore(data, this);
			}
		},

		saveMeetingOrder(meetingId, order) {
			if (!config.RACING_ENABLED) return;

			return _store
				.firstWhere('id', meetingId)
				.update({ order: order })
				.then((results) => {
					// keep our local collection in sync
					update((meetings) => {
						return meetings.each((meeting) => {
							if (meeting.id === meetingId) {
								meeting.order = order;
							}
							return meeting;
						});
					});

					return results;
				});
		},

		/**
		 * ----------------- STORE METHODS -----------------
		 */

		getNextMeetingId() {
			const nextToJumpMeeting = RaceStore.getMeetingIdForNextRace();
			if (nextToJumpMeeting) {
				return nextToJumpMeeting;
			}

			return _store.sortBy('start_time').first()?.id ?? undefined;
		}
	};

	return {
		subscribe,
		update,
		set,
		...methods
	};
};

export default init();
